import { Loadable } from 'components/common';
import { useOmniHistoriesLeadManageContext } from 'contexts';
import { twMerge } from 'tailwind-merge';
import OmniChatSessionsWaiting, { OmniChatSessionsWaitingRefProps } from './OmniChatSessionsWaiting';
import { ChatSessionDto, UserCompactDto } from 'types';
import { useEffect, useRef, useState } from 'react';
import OmniChatSessionWaitingAssignUser from './OmniChatSessionWaitingAssignUser';
import { Button, Modal, Result, Typography } from 'antd';
import { messages, omniChatMessages } from 'messages';
import Avatar from 'components/common/avatar/Avatar';
import OmniChatSessionsWaitingAssignItem from './OmniChatSessionsWaitingAssignItem';
import { useAssignChatSession, useProfile } from 'hooks';
import { onReceiveChatSessionWaiting } from 'services';
import { debounce } from 'lodash';
import { CHAT_STATUS, PERMISSION } from 'utils';
import { OmniChannelLeadChatHistoriesChatBox } from 'components/omni-channel-chat-histories';
import { checkSomePermissions } from 'utils/check-permission';

type AssignManagementTabProps = {
  onSelectCustomer?: (value?: ChatSessionDto) => void;
  selectedCustomerId?: number;
};

const AssignManagementTab: React.FC<AssignManagementTabProps> = ({ onSelectCustomer, selectedCustomerId }) => {
  const { chatSession, setChatSession } = useOmniHistoriesLeadManageContext();
  const { onAssignChatSession, isLoading } = useAssignChatSession();
  const [userAssign, setUserAssign] = useState<UserCompactDto | undefined>();
  const [assign, setAssign] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const waitingRef = useRef<OmniChatSessionsWaitingRefProps>(null);
  const { userPermissionIds } = useProfile();

  const isAllowAssign = checkSomePermissions(userPermissionIds, [PERMISSION.ASSIGN_CHAT_CUSTOMER]);

  const endpoint = 'assign-chat-session';

  const handleReset = () => {
    setChatSession(undefined);
    setAssign(false);
    setUserAssign(undefined);
  };
  const cancelConfirmAssign = () => {
    setOpenConfirm(false);
  };

  const confirmAssign = (user: UserCompactDto) => {
    setUserAssign(user);
    setOpenConfirm(true);
  };

  const handleAssignChatSession = () => {
    if (chatSession && userAssign) {
      onAssignChatSession(chatSession.chatSessionId, userAssign.userId).then(() => {
        setOpenConfirm(false);
        handleReset();
        if (waitingRef.current) {
          waitingRef.current.handleRefetchWaiting();
        }
      });
    }
  };

  useEffect(() => {
    onReceiveChatSessionWaiting((data) => {
      if (data && data.chatSessionId === chatSession?.chatSessionId) {
        debounce(() => {
          if (data.status !== CHAT_STATUS.WAITING) {
            handleReset();
          }
        }, 100)();
      }
    });
  }, []);

  return (
    <>
      {isAllowAssign ? (
        <div className='flex h-full flex-1'>
          <div className={twMerge('mx-auto h-full w-[360px] shrink-0 2xl:w-[468px]', chatSession ? '' : 'border-x')}>
            <OmniChatSessionsWaiting
              ref={waitingRef}
              endpoint={endpoint}
              onSelectCustomer={(customerChat) => {
                setAssign(false);
                setUserAssign(undefined);
                setChatSession(customerChat);
              }}
              selectedCustomerId={chatSession?.chatSessionId}
            />
          </div>
          {chatSession && (
            <div
              className={twMerge(
                'h-full w-full border-r transition-all duration-200',
                chatSession && !assign ? 'translate-x-0' : assign ? '-translate-x-full' : 'translate-x-full'
              )}
            >
              <Loadable>
                <OmniChannelLeadChatHistoriesChatBox chatSession={chatSession} />
              </Loadable>
            </div>
          )}
          {chatSession && (
            <div
              className={twMerge(
                'h-full w-[360px] shrink-0 rounded-xl bg-white transition-all duration-200 2xl:w-[468px]',
                chatSession ? 'translate-x-0' : 'translate-x-full'
              )}
            >
              <OmniChatSessionWaitingAssignUser
                selectedUserId={userAssign?.userId}
                onSelectUser={(userId, user) => confirmAssign(user)}
                onBack={() => {
                  setAssign(false);
                  setUserAssign(undefined);
                }}
              />
            </div>
          )}
          <Modal
            centered
            className='w-[468px] rounded-xl bg-[#F5F4F6]'
            open={openConfirm}
            onCancel={cancelConfirmAssign}
            destroyOnClose
            styles={{ body: { padding: 40 } }}
            footer={
              <Button type='primary' onClick={handleAssignChatSession} className='w-[136px]' loading={isLoading}>
                {messages.confirmButtonText}
              </Button>
            }
          >
            <div className='pb-4'>
              <Typography.Title className='my-0 text-center text-[22px]'>
                {omniChatMessages.confirmAssign}
              </Typography.Title>
            </div>
            <div className='pb-8'>
              <p className='text-center'>
                <Typography.Text className='my-0 mr-2'>{omniChatMessages.assignDTV}</Typography.Text>
                <Avatar key={userAssign?.userId} size={20} src={userAssign?.avatar} name={userAssign?.fullName} />
                <Typography.Text className='mx-2 my-0 font-semibold'>{userAssign?.fullName}</Typography.Text>
                <Typography.Text className='my-0'>{omniChatMessages.supportCustomer}</Typography.Text>
              </p>
            </div>
            <OmniChatSessionsWaitingAssignItem chatSession={chatSession!} readonly />
          </Modal>
        </div>
      ) : (
        <Result status='403' title='403' subTitle={messages.notAllowToAccess} />
      )}
    </>
  );
};

export default AssignManagementTab;
